import type { CampaignAnalysisHistoryItem } from '@/entities/campaign/generated/campaign-type';
import { Button } from '@/shared/components/shadcn/button';
import { formatDate } from '@/shared/utils/date/date-formats';
import { Loader2 } from 'lucide-react';

import {
  getCampaignAnalysisRunTitle,
  getCampaignAnalysisStatusLabel,
} from '../helpers/analysis-history';
import { cn } from '@/shared/utils';

type Props = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isNextPageError: boolean;
  onLoadMore: () => void;
  onSelectRun: (runId: string) => void;
  runs: readonly CampaignAnalysisHistoryItem[];
  selectedRunId: string;
};

export function CampaignAnalysisHistory({
  hasNextPage,
  isFetchingNextPage,
  isNextPageError,
  onLoadMore,
  onSelectRun,
  runs,
  selectedRunId,
}: Props) {
  return (
    <aside aria-label="Analysis history" className="lg:sticky lg:top-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          {runs.map((run) => (
            <CampaignAnalysisHistoryItem
              key={run.run_id}
              isSelected={run.run_id === selectedRunId}
              onSelect={() => onSelectRun(run.run_id)}
              run={run}
            />
          ))}
        </div>

        {isNextPageError ? (
          <p className="text-destructive px-3 text-xs leading-5" role="alert">
            Older analyses could not be loaded. Try again.
          </p>
        ) : null}

        {hasNextPage || isNextPageError ? (
          <Button
            className="w-full"
            disabled={isFetchingNextPage}
            loading={isFetchingNextPage}
            onClick={onLoadMore}
            size="sm"
            variant="outline"
          >
            Load older analyses
          </Button>
        ) : null}
      </div>
    </aside>
  );
}

function CampaignAnalysisHistoryItem({
  isSelected,
  onSelect,
  run,
}: {
  isSelected: boolean;
  onSelect: () => void;
  run: CampaignAnalysisHistoryItem;
}) {
  const isRunning = run.status === 'requesting' || run.status === 'processing';
  const historyMeta = isRunning
    ? getCampaignAnalysisStatusLabel(run.status)
    : run.status === 'failed'
      ? getCampaignAnalysisStatusLabel(run.status)
      : formatDate(run.created_at);

  return (
    <button
      aria-current={isSelected ? 'true' : undefined}
      aria-pressed={isSelected}
      className={cn(
        'focus-visible:ring-ring/50 w-full rounded-xl px-3 py-2.5 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none',
        isSelected
          ? 'bg-foreground text-background shadow-sm'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
      onClick={onSelect}
      type="button"
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-sm font-medium">{getCampaignAnalysisRunTitle(run)}</span>
          <span className="text-xs">{historyMeta}</span>
        </div>
        {isRunning ? (
          <span aria-label="In progress" className="inline-flex shrink-0">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          </span>
        ) : null}
      </div>
    </button>
  );
}
